import type { SortingOrder } from "@/graphql/codegen/graphql"
import { OrderLineSortField, OrderSortField } from "@/graphql/codegen/graphql"

export enum Categories {
  Supplier = "supplier",
  Product = "product",
}

export interface SearchFilters {
  query?: string
  filter?: Record<string, unknown>
  field?: OrderLineSortField
  order?: SortingOrder
  [key: string]: unknown
}

// [Supplier] Table accessor keys
export const SortablePurchaseOrderAccessorKeys = {
  companyName: "companyName",
  orderId: "orderId",
  status: "status",
  shipStatus: "shipStatus",
  requestedDeliveryDate: "requestedDeliveryDate",
  expectedDeliveryDate: "expectedDeliveryDate",
  productId: "productId",
  companyProductDescription: "companyProductDescription",
}
export type SortablePurchaseOrderAccessorKey =
  (typeof SortablePurchaseOrderAccessorKeys)[keyof typeof SortablePurchaseOrderAccessorKeys]

// [Product] Table accessor keys
export const SortableOrderLineAccessorKeys = {
  orderId: "orderId",
  status: "status",
  shipStatus: "shipStatus",
  requestedDeliveryDate: "orderDetail_order_requestedDeliveryDate",
  expectedDeliveryDate: "orderDetail_order_expectedDeliveryDate",
  productId: "productId",
  companyProductDescription: "companyProductDescription",
}
export type SortableOrderLineAccessorKey =
  (typeof SortableOrderLineAccessorKeys)[keyof typeof SortableOrderLineAccessorKeys]

// Used for to map url params to table cells
export const orderSortFieldToOrderLineAccessoryKey: ReadonlyMap<OrderSortField, SortableOrderLineAccessorKey> = new Map(
  [
    [OrderSortField.CompanyName, SortableOrderLineAccessorKeys.companyProductDescription],
    [OrderSortField.OrderId, SortableOrderLineAccessorKeys.orderId],
    [OrderSortField.RequestedDate, SortableOrderLineAccessorKeys.requestedDeliveryDate],
    [OrderSortField.ExpectedDate, SortableOrderLineAccessorKeys.expectedDeliveryDate],
    [OrderSortField.Status, SortableOrderLineAccessorKeys.status],
    [OrderSortField.ShipStatus, SortableOrderLineAccessorKeys.shipStatus],
    [OrderSortField.ProductId, SortableOrderLineAccessorKeys.productId],
  ]
)

export const orderSortFieldToPurchaseOrderAccessoryKey: ReadonlyMap<OrderSortField, SortablePurchaseOrderAccessorKey> =
  new Map([
    [OrderSortField.CompanyName, SortablePurchaseOrderAccessorKeys.companyName],
    [OrderSortField.OrderId, SortablePurchaseOrderAccessorKeys.orderId],
    [OrderSortField.RequestedDate, SortablePurchaseOrderAccessorKeys.requestedDeliveryDate],
    [OrderSortField.ExpectedDate, SortablePurchaseOrderAccessorKeys.expectedDeliveryDate],
    [OrderSortField.Status, SortablePurchaseOrderAccessorKeys.status],
    [OrderSortField.ShipStatus, SortablePurchaseOrderAccessorKeys.shipStatus],
  ])

// Used for to map table cells to url params
export const accessoryKeyToOrderSortField: ReadonlyMap<SortablePurchaseOrderAccessorKey, OrderSortField> = new Map([
  // [Supplier] tab
  [SortablePurchaseOrderAccessorKeys.companyName, OrderSortField.CompanyName],
  [SortablePurchaseOrderAccessorKeys.orderId, OrderSortField.OrderId],
  [SortablePurchaseOrderAccessorKeys.requestedDeliveryDate, OrderSortField.RequestedDate],
  [SortablePurchaseOrderAccessorKeys.expectedDeliveryDate, OrderSortField.ExpectedDate],
  [SortablePurchaseOrderAccessorKeys.status, OrderSortField.Status],
  [SortablePurchaseOrderAccessorKeys.shipStatus, OrderSortField.ShipStatus],

  // [Product] tab
  [SortableOrderLineAccessorKeys.productId, OrderSortField.ProductId],
  [SortableOrderLineAccessorKeys.companyProductDescription, OrderSortField.CompanyName],
  [SortableOrderLineAccessorKeys.requestedDeliveryDate, OrderSortField.RequestedDate],
  [SortableOrderLineAccessorKeys.expectedDeliveryDate, OrderSortField.ExpectedDate],
  [SortableOrderLineAccessorKeys.status, OrderSortField.Status],
  [SortableOrderLineAccessorKeys.shipStatus, OrderSortField.ShipStatus],
])

// Use this mapping to be able to switch between Supplier and Product tab and keep same filters
export const adapterOrderSortFieldToOrderLineSortField: ReadonlyMap<OrderSortField, OrderLineSortField> = new Map([
  [OrderSortField.CompanyName, OrderLineSortField.ProductSupplierDescription],
  [OrderSortField.ExpectedDate, OrderLineSortField.OrderExpectedDate],
  [OrderSortField.OrderId, OrderLineSortField.OrderId],
  [OrderSortField.RequestedDate, OrderLineSortField.OrderRequestedDate],
  [OrderSortField.ShipStatus, OrderLineSortField.OrderShipStatus],
  [OrderSortField.Status, OrderLineSortField.OrderStatus],
  [OrderSortField.ProductId, OrderLineSortField.ProductId],
  [OrderSortField.UpdatedOn, OrderLineSortField.OrderCreatedOn],
])
